import { useState } from 'react'
import { App as AntdApp, Card, Col, Row, Statistic, Table, Button, Space, Typography, Divider, Tag, Alert } from 'antd'
import {
  MessageOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SyncOutlined,
  SendOutlined,
  BookOutlined,
  CloseCircleOutlined,
  BugOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useDashboardSummary, usePilotDashboard } from '@/modules/dashboard/queries'
import { useMessageList } from '@/modules/message/queries'
import { useDraftList, useGenerateDraft } from '@/modules/draft/queries'
import { useSlaEventList } from '@/modules/sla/queries'
import { getSlaRuleName } from '@/modules/sla/labels'
import { formatDateTime, formatMessageTime, formatShortDateTime } from '@/utils/datetime'
import StatusTag from '@/components/feedback/StatusTag'
import ErrorResult from '@/components/feedback/ErrorResult'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { canGenerateDraftFromMessage } from '@/pages/messages/messageDraftRules'
import type { MessageItem } from '@/types/message'
import type { SlaEventItem } from '@/types/sla'

const { Text } = Typography

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : '操作失败'
}

export default function DashboardPage() {
  const { message } = AntdApp.useApp()
  const navigate = useNavigate()
  const hasPermission = useAuthStore((s) => s.hasPermission)
  const generateDraft = useGenerateDraft()
  const [generatingDraftMessageId, setGeneratingDraftMessageId] = useState<string | null>(null)
  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
    error: summaryErrorDetail,
  } = useDashboardSummary()
  const { data: pilot, isLoading: pilotLoading } = usePilotDashboard()
  const { data: messages } = useMessageList({ page: 1, page_size: 5, message_status: 'pending_response' })
  const { data: drafts } = useDraftList({ page: 1, page_size: 5, status: 'drafted' })
  const { data: slaEvents } = useSlaEventList({ page: 1, page_size: 5, event_status: 'open' })

  const isSummaryForbidden =
    summaryErrorDetail instanceof Error &&
    'kind' in summaryErrorDetail &&
    String((summaryErrorDetail as { kind?: string }).kind || '') === 'forbidden'

  async function generateDraftFromMessage(row: MessageItem) {
    setGeneratingDraftMessageId(row.message_id)
    const messageKey = `dashboard-generate-draft-${row.message_id}`
    message.loading({ content: '正在生成草稿...', key: messageKey, duration: 0 })
    try {
      const result = await generateDraft.mutateAsync(row.message_id)
      if (!result.id) throw new Error('后端未返回草稿ID')
      message.success({ content: '草稿生成成功，正在打开...', key: messageKey })
      navigate(`/drafts/${result.id}`)
    } catch (error) {
      message.error({ content: `草稿生成失败：${errorMessage(error)}`, key: messageKey, duration: 5 })
    } finally {
      setGeneratingDraftMessageId(null)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 统计卡 —— summary 403 时仅降级隐藏此区域，不崩溃整页 */}
      {!summaryError && (
        <Row gutter={16}>
          <Col flex={1}>
            <Card size="small">
              <Statistic
                title="今日新消息"
                value={summary?.today_new_message_count ?? 0}
                prefix={<MessageOutlined />}
                loading={summaryLoading}
              />
            </Card>
          </Col>
          <Col flex={1}>
            <Card size="small">
              <Statistic
                title="待响应"
                value={summary?.pending_response_count ?? 0}
                prefix={<MessageOutlined />}
                styles={{ content: { color: summary?.pending_response_count ? '#faad14' : undefined } }}
                loading={summaryLoading}
              />
            </Card>
          </Col>
          <Col flex={1}>
            <Card size="small">
              <Statistic
                title="待确认草稿"
                value={summary?.pending_draft_confirm_count ?? 0}
                prefix={<FileTextOutlined />}
                styles={{ content: { color: summary?.pending_draft_confirm_count ? '#1677ff' : undefined } }}
                loading={summaryLoading}
              />
            </Card>
          </Col>
          <Col flex={1}>
            <Card size="small">
              <Statistic
                title="待客户确认"
                value={summary?.waiting_customer_confirm_count ?? 0}
                prefix={<CheckCircleOutlined />}
                styles={{ content: { color: summary?.waiting_customer_confirm_count ? '#722ed1' : undefined } }}
                loading={summaryLoading}
              />
            </Card>
          </Col>
          <Col flex={1}>
            <Card size="small">
              <Statistic
                title="响应超时"
                value={summary?.open_sla_event_count ?? 0}
                prefix={<WarningOutlined />}
                styles={{ content: { color: summary?.open_sla_event_count ? '#ff4d4f' : undefined } }}
                loading={summaryLoading}
              />
            </Card>
          </Col>
          <Col flex={1}>
            <Card size="small">
              <Statistic
                title="近期失败任务"
                value={summary?.recent_job_failure_count ?? 0}
                prefix={<BugOutlined />}
                styles={{ content: { color: (summary?.recent_job_failure_count ?? 0) > 0 ? '#ff4d4f' : undefined } }}
                loading={summaryLoading}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* summary 返回 403 时显示降级提示；其他错误展示真实原因 */}
      {summaryError && isSummaryForbidden && (
        <Alert
          type="warning"
          showIcon
          title="运营概览不可用"
          description="当前角色无权访问运营概览数据，试运行看板仍可正常查看。"
          style={{ marginBottom: 0 }}
        />
      )}
      {summaryError && !isSummaryForbidden && (
        <ErrorResult error={summaryErrorDetail} />
      )}

      {/* ── T29 试运行指标卡 ── */}
      <Divider titlePlacement="left" style={{ margin: '8px 0' }}>
        <Text strong style={{ fontSize: 14 }}>试运行数据看板</Text>
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
          {pilot?.period?.from ? `${pilot.period.from.slice(0, 10)} ~ ${pilot.period.to.slice(0, 10)}` : ''}
        </Text>
      </Divider>

      <Row gutter={16}>
        <Col flex={1}>
          <Card size="small">
            <Statistic
              title="新增消息"
              value={pilot?.summary?.total_messages ?? 0}
              prefix={<MessageOutlined />}
              loading={pilotLoading}
            />
          </Card>
        </Col>
        <Col flex={1}>
          <Card size="small">
            <Statistic
              title="草稿"
              value={pilot?.summary?.total_drafts ?? 0}
              prefix={<FileTextOutlined />}
              loading={pilotLoading}
            />
          </Card>
        </Col>
        <Col flex={1}>
          <Card size="small">
            <Statistic
              title="草稿转化率"
              value={pilot?.summary?.draft_conversion_rate ?? 0}
              precision={1}
              suffix="%"
              formatter={(v) => (Number(v) * 100).toFixed(1)}
              prefix={<CheckCircleOutlined />}
              styles={{ content: { color: (pilot?.summary?.draft_conversion_rate ?? 0) > 0 ? '#1677ff' : undefined } }}
              loading={pilotLoading}
            />
          </Card>
        </Col>
        <Col flex={1}>
          <Card size="small">
            <Statistic
              title="建单成功率"
              value={pilot?.summary?.ticket_creation_success_rate ?? 0}
              precision={1}
              suffix="%"
              formatter={(v) => (Number(v) * 100).toFixed(1)}
              prefix={<CheckCircleOutlined />}
              styles={{ content: { color: (pilot?.summary?.ticket_creation_success_rate ?? 0) > 0 ? '#52c41a' : undefined } }}
              loading={pilotLoading}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col flex={1}>
          <Card size="small">
            <Statistic
              title="超时提醒"
              value={pilot?.summary?.total_sla_events ?? 0}
              prefix={<WarningOutlined />}
              loading={pilotLoading}
            />
          </Card>
        </Col>
        <Col flex={1}>
          <Card size="small">
            <Statistic
              title="未解决超时"
              value={pilot?.summary?.sla_breach_count ?? 0}
              prefix={<CloseCircleOutlined />}
              styles={{ content: { color: (pilot?.summary?.sla_breach_count ?? 0) > 0 ? '#ff4d4f' : undefined } }}
              loading={pilotLoading}
            />
          </Card>
        </Col>
        <Col flex={1}>
          <Card size="small">
            <Statistic
              title="同步失败"
              value={pilot?.summary?.sync_failures ?? 0}
              prefix={<SyncOutlined />}
              styles={{ content: { color: (pilot?.summary?.sync_failures ?? 0) > 0 ? '#ff4d4f' : undefined } }}
              loading={pilotLoading}
            />
          </Card>
        </Col>
        <Col flex={1}>
          <Card size="small">
            <Statistic
              title="通知失败"
              value={pilot?.summary?.notification_failures ?? 0}
              prefix={<SendOutlined />}
              styles={{ content: { color: (pilot?.summary?.notification_failures ?? 0) > 0 ? '#ff4d4f' : undefined } }}
              loading={pilotLoading}
            />
          </Card>
        </Col>
        <Col flex={1}>
          <Card size="small">
            <Statistic
              title="知识沉淀"
              value={pilot?.summary?.knowledge_entries ?? 0}
              prefix={<BookOutlined />}
              loading={pilotLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* 最近失败任务 */}
      {(pilot?.recent_failed_jobs?.length ?? 0) > 0 && (
        <Card
          size="small"
          title={
            <Space>
              <Text style={{ color: '#ff4d4f' }}>最近失败任务</Text>
              <Tag color="error">{pilot?.recent_failed_jobs?.length}</Tag>
            </Space>
          }
          extra={
            <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => navigate('/system/job-runs')}>
              查看全部任务
            </Button>
          }
        >
          <Table
            size="small"
            pagination={false}
            rowKey="run_id"
            dataSource={pilot?.recent_failed_jobs || []}
            loading={pilotLoading}
            columns={[
              { title: 'Run ID', dataIndex: 'run_id', width: 180, ellipsis: true },
              { title: '任务', dataIndex: 'job_code', width: 180 },
              { title: '开始时间', dataIndex: 'started_at', width: 170, render: (v: string) => formatDateTime(v) },
              { title: '错误信息', dataIndex: 'error_message', ellipsis: true, render: (v: string | null) => <Text type="danger">{v || '-'}</Text> },
            ]}
          />
        </Card>
      )}

      <Divider titlePlacement="left" style={{ margin: '8px 0' }}>
        <Text strong style={{ fontSize: 14 }}>运营概览</Text>
      </Divider>

      {/* 待响应消息 */}
      <Card size="small" title="待响应消息">
        <Table<MessageItem>
          size="small"
          pagination={false}
          rowKey="message_id"
          dataSource={messages?.items || []}
          columns={[
            { title: '时间', dataIndex: 'send_time', width: 120, render: (v: string) => formatMessageTime(v) },
            { title: '客户', dataIndex: 'customer_name', width: 100, render: (v: string) => v || <Text type="secondary">未绑定</Text> },
            { title: '摘要', dataIndex: 'content_preview', ellipsis: true },
            { title: '状态', dataIndex: 'message_status', width: 90, render: (v: string) => <StatusTag type="message" status={v} /> },
            {
              title: '操作', width: 150, render: (_: unknown, r: MessageItem) => (
                <Space size="small">
                  <Button type="link" size="small" onClick={() => navigate(`/messages?messageId=${encodeURIComponent(r.message_id)}`)}>
                    去响应
                  </Button>
                  {canGenerateDraftFromMessage(r.message_status, r.operator_actions) && hasPermission('draft:update') && (
                    <Button
                      type="link"
                      size="small"
                      loading={generatingDraftMessageId === r.message_id}
                      onClick={() => { void generateDraftFromMessage(r) }}
                    >
                      草稿
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
        />
        <div style={{ textAlign: 'right', marginTop: 8 }}>
          <Button type="link" onClick={() => navigate('/messages')}>查看全部消息</Button>
        </div>
      </Card>

      {/* 待确认草稿 */}
      <Card size="small" title="待确认草稿">
        <Table
          size="small"
          pagination={false}
          rowKey="id"
          dataSource={drafts?.items || []}
          columns={[
            { title: '编号', dataIndex: 'draft_no', width: 140 },
            { title: '客户', dataIndex: 'customer_name', width: 100, render: (v: string) => v || <Text type="secondary">未绑定</Text> },
            { title: '标题', dataIndex: 'task_title', ellipsis: true },
            { title: '优先级', dataIndex: 'priority', width: 60 },
            { title: '状态', dataIndex: 'status', width: 90, render: (v: string) => <StatusTag type="draft" status={v} /> },
            {
              title: '操作', width: 100, render: (_: unknown, r: { id: string }) => (
                <Button type="link" size="small" onClick={() => navigate(`/drafts/${r.id}`)}>继续审核</Button>
              ),
            },
          ]}
        />
        <div style={{ textAlign: 'right', marginTop: 8 }}>
          <Button type="link" onClick={() => navigate('/drafts')}>查看全部草稿</Button>
        </div>
      </Card>

      {/* 响应时限提醒 */}
      <Card size="small" title="响应时限提醒">
        <Table<SlaEventItem>
          size="small"
          pagination={false}
          rowKey="id"
          dataSource={slaEvents?.items || []}
          columns={[
            {
              title: '超时类型',
              dataIndex: 'rule_code',
              width: 140,
              render: (ruleCode: string, r: { rule_name: string }) => getSlaRuleName(ruleCode, r.rule_name),
            },
            { title: '工单', dataIndex: 'task_title', ellipsis: true, render: (v: string) => v || <Text type="secondary">-</Text> },
            { title: '责任人', dataIndex: 'target_account', width: 100 },
            { title: '命中时间', dataIndex: 'first_detected_at', width: 120, render: (v: string) => formatShortDateTime(v) },
            { title: '状态', dataIndex: 'event_status', width: 90, render: (v: string) => <StatusTag type="sla" status={v} /> },
            {
              title: '操作', width: 100, render: (_: unknown, r: SlaEventItem) => (
                <Button type="link" size="small" onClick={() => navigate(`/sla?eventId=${encodeURIComponent(r.id)}`)}>
                  处理
                </Button>
              ),
            },
          ]}
        />
        <div style={{ textAlign: 'right', marginTop: 8 }}>
          <Button type="link" onClick={() => navigate('/sla')}>查看全部提醒</Button>
        </div>
      </Card>
    </div>
  )
}
